import React from "react";
import { GmbHDetails } from "lib/models/client/GmbHDetails";
import { UpdateWepaServiceVariables } from "lib/api/mutations/graphql/UpdateWepaService";
import RideDatePicker from "sharedComponents/RideDatePicker/RideDatePicker";
import { ValueView } from "components/common/layout/ValueView";
import { formatDate } from "common/formatters";
import EditableSectionWithFormik from "sharedComponents/EditableSectionWithFormik/EditableSectionWithFormik";
import { useFormik } from "formik";
import { Company } from "lib/types/types";
import { TFunction } from "i18next";
import { withTranslationReady } from "common/i18n/withTranslationReady";
import { WithTranslation } from "react-i18next";

interface Props extends WithTranslation {
  gmbh: GmbHDetails;
  updateWepaService: (data: UpdateWepaServiceVariables) => Promise<void>;
  refetch: () => Promise<any>;
}

const WepaServiceDetails = ({ t, gmbh, updateWepaService, refetch }: Props) => {
  const wepaService = gmbh.wepaService;

  if (!wepaService?.id) return null;

  return (
    <WepaServiceDetailsForm
      t={t}
      wepaService={wepaService}
      updateWepaService={updateWepaService}
      refetch={refetch}
    />
  );
};

const WepaServiceDetailsForm = ({
  t,
  wepaService,
  updateWepaService,
  refetch
}: {
  t: TFunction;
  wepaService: NonNullable<Company["wepaService"]>;
  updateWepaService: (data: UpdateWepaServiceVariables) => Promise<void>;
  refetch: () => Promise<any>;
}) => {
  const handleSubmit = async (data: UpdateWepaServiceVariables) => {
    await updateWepaService(data);
    await refetch();
  };

  const initialValues: UpdateWepaServiceVariables = {
    id: wepaService.id!,
    canceledAt: wepaService.canceledAt
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true
  });

  return (
    <EditableSectionWithFormik
      formik={formik}
      title={t("nominee-dashboard:wepa-service.details")}
      testId="wepaServiceDetails">
      {({ isEditMode }) =>
        isEditMode ? (
          <RideDatePicker
            name={"canceledAt"}
            label={t("nominee-dashboard:wepa-service.activatedAt")}
            selectedDate={formik.values["canceledAt"]}
            onSelectDate={(value) => formik.setFieldValue("canceledAt", value)}
          />
        ) : (
          <ValueView
            name="canceledAt"
            value={formatDate(wepaService.canceledAt)}
            testId="canceledAt"
            t={t}
          />
        )
      }
    </EditableSectionWithFormik>
  );
};

export default withTranslationReady(["nominee-dashboard"])(WepaServiceDetails);
